/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import { screen } from "@eisinc/XDL/Helpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView, OrdersDataViewContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import { Icon } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
    showReorderProducts = "showReorderProducts",
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
        [fields.showReorderProducts]: boolean;
        [fields.title]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    const settingsCollection = getSettingsCollection(state);
    const shouldShowStatus =
        ((settingsCollection as any).customSettings?.orderHistoryListingPageShowStatusFilter || "").toLowerCase() ===
        "true";
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        language: state.context.session.language,
        parameter: state.pages.orderHistory.getOrdersParameter,
        isReordering: state.pages.orderHistory.isReordering,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        shouldShowStatus,
    };
};

const mapDispatchToProps = {
    loadOrders,
    updateSearchFields,
    reorder,
};

type Props = ReturnType<typeof mapStateToProps> &
    OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

const defaultCustomCss = `.card-list-heading{
    }
    
    .view-all-link{
    }
    
    .no-orders-found-grid-item{
    }
    
    .description-grid-item{
    }
    
    .no-orders-found-text{
    }
    
    .card_container__wrapper{
    }
    
    .order-summary-card-grid-item{
    }
    
    .order-summary-card-container{
    }
    
    .page-type-link-grid-item{
    }
    
    .order-number-grid-item{
    }
    
    .status-grid-item{
    }
    
    .web-order-number-grid-item{
    }
    
    .total-grid-item{
    }`;

// changing this to match the key of orders in the store
let recentOrdersParameter: GetOrdersApiParameter = {
    customerSequence: "-1",
    pageSize: 5,
    sort: "OrderDate DESC",
};

export interface RecentOrdersStyles {
    errorText?: TypographyPresentationProps;
    noOrdersFoundText?: TypographyPresentationProps;
    container?: InjectableCss;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    orderDateHeader?: DataTableHeaderProps;
    orderNumberHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    reorderHeader?: DataTableHeaderProps;
    orderDateCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    orderTotalCells?: DataTableCellProps;
    reorderCells?: DataTableCellProps;
    reorderButton?: ButtonPresentationProps;
    reorderButtonSpinner?: LoadingSpinnerProps;
    orderNumberLink?: LinkPresentationProps;
    dataTable?: DataTableProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
}

const recentOrdersStyles: RecentOrdersStyles = {
    container: {
        css: css`
            overflow: auto;
        `,
    },
    spinner: {
        color: "primary.main",
        css: css`
            margin: auto;
        `,
    },
    reorderButton: {
        variant: "primary",
        sizeVariant: "small",
        css: css`
            display: flex;
            align-items: center;
            width: 48px;
            height: 40px;
            min-height: 40px;
            justify-content: center;
            padding: 0;

            svg {
                color: ${getColor("common.white")};
            }
        `,
    },
    reorderButtonSpinner: {
        color: "primary.main",
        size: 22,
        css: css`
            margin-left: auto;
            margin-right: auto;
            display: block;
        `,
    },
    shipToHeader: {
        tight: true,
    },
    orderTotalHeader: {
        tight: true,
        alignX: "left",
    },
    reorderHeader: {
        tight: true,
    },
    orderTotalCells: {
        alignX: "left",
    },
    statusCells: {
        alignX: "left",
    },
    shipToCells: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 146px;

                ${screen("gt", "lg")`
                    max-width: 300px;
                `}
            `,
        },
    },
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    static contextType = OrdersDataViewContext;

    declare context: React.ContextType<typeof OrdersDataViewContext>;

    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;

        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    headerClick(sortField: string) {
        const sort = this.props.parameter.sort === sortField ? `${sortField} DESC` : sortField;

        recentOrdersParameter.sort = sort;

        this.props.updateSearchFields({ sort });
        this.props.loadOrders(recentOrdersParameter);
    }

    sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (this.props.parameter.sort === sortField) {
            sorted = "ascending";
        } else if (this.props.parameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }

        return sorted as SortOrderOptions;
    };

    onReorderSuccess = (orderNumber: string, linkOrderNumber: string) => {
        if (!this.props.showAddToCartConfirmationDialog) {
            return;
        }

        this.props.toaster.addToast({
            children: (
                <>
                    <OrderDetailPageTypeLink title={orderNumber} orderNumber={linkOrderNumber} />
                    &nbsp;{translate("added to cart")}
                </>
            ),
            messageType: "success",
            timeoutLength: 6000,
        });
    };

    render() {
        const { ordersDataView, orderHistoryPageLink, fields, shouldShowStatus } = this.props;
        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;
        const orders = ordersDataView.value;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        if (!this.props.canViewOrders) {
            return null;
        }

        if (!orders) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!orders.length || !this.props.canViewOrders) {
            return null;
        }

        const rows = orders.map((order: any) => {
            return {
                id: order.id,
                linkOrderNumber: order.webOrderNumber || order.erpOrderNumber,
                date: order.orderDate
                    ? getLocalizedDateTime({
                          dateTime: new Date(order.orderDate),
                          language: this.props.language,
                      })
                    : "",
                orderNumber: order.erpOrderNumber ? order.erpOrderNumber : translate("Pending"),
                shipTo: `${order.stCompanyName} ${order.btAddress1} ${order.btAddress2} ${order.shipToCity} ${order.shipToState}`,
                status: order.statusDisplay,
                po: order.customerPO,
                total: order.orderGrandTotalDisplay,
                reorderOrderNumber: order.erpOrderNumber || order.webOrderNumber,
            };
        });

        // XNG-change - EIS-4192 - replace div with semantic HTML elements
        const SectionWrapper = styled.section`
            ${customCssWrapper.css}
        `;

        return (
            <SectionWrapper aria-label={fields.title}>
                <StyledWrapper
                    css={css`
                        width: 100%;
                        display: flex;
                        justify-content: space-between;
                    `}
                >
                    <Typography variant="h2">{fields.title}</Typography>
                    <Link href={orderHistoryUrl}>{translate("View All")}</Link>
                </StyledWrapper>
                <StyledWrapper {...styles.container} data-test-selector="orderHistoryTable">
                    <DataTable {...styles.dataTable}>
                        <DataTableHead>
                            <DataTableHeader
                                {...styles.orderNumberHeader}
                                title={translate("Order Number")}
                                sorted={this.sorted("webOrderNumber")}
                                onSortClick={() => this.headerClick("webOrderNumber")}
                            >
                                {translate("Order #", "webOrderNumber")}
                            </DataTableHeader>
                            <DataTableHeader
                                {...styles.orderDateHeader}
                                sorted={this.sorted("orderDate")}
                                onSortClick={() => this.headerClick("orderDate")}
                            >
                                {translate("Order Date", "orderDate")}
                            </DataTableHeader>
                            <DataTableHeader
                                {...styles.customerPOHeader}
                                title={translate("Purchase Order Number")}
                                sorted={this.sorted("customerPO")}
                                onSortClick={() => this.headerClick("customerPO")}
                            >
                                {translate("PO/Ref #", "customerPO")}
                            </DataTableHeader>
                            <DataTableHeader
                                {...styles.shipToHeader}
                                sorted={this.sorted("stCompanyName")}
                                onSortClick={() => this.headerClick("stCompanyName")}
                            >
                                {translate("Ship To / Pick Up", "stCompanyName")}
                            </DataTableHeader>
                            {shouldShowStatus ? (
                                <DataTableHeader
                                    {...styles.statusHeader}
                                    sorted={this.sorted("status")}
                                    onSortClick={() => this.headerClick("status")}
                                >
                                    {translate("Status", "status")}
                                </DataTableHeader>
                            ) : null}
                            <DataTableHeader
                                {...styles.orderTotalHeader}
                                sorted={this.sorted("orderTotal")}
                                onSortClick={() => this.headerClick("orderTotal")}
                            >
                                {translate("Order Total", "orderTotal")}
                            </DataTableHeader>

                            {this.props.fields.showReorderProducts && (
                                <DataTableHeader {...styles.reorderHeader} title={translate("reorder")}>
                                    {translate("Actions", "actions")}
                                </DataTableHeader>
                            )}
                        </DataTableHead>
                        <DataTableBody data-test-selector="orderHistoryTable_tableBody">
                            {rows?.map(
                                ({
                                    id,
                                    linkOrderNumber,
                                    date,
                                    orderNumber,
                                    shipTo,
                                    status,
                                    po,
                                    total,
                                    reorderOrderNumber,
                                }) => {
                                    return (
                                        <DataTableRow key={id}>
                                            <DataTableCell
                                                {...styles.orderNumberCells}
                                                data-test-selector="orderHistoryTable_tableCell_orderNumber"
                                            >
                                                <OrderDetailPageTypeLink
                                                    title={orderNumber}
                                                    orderNumber={linkOrderNumber}
                                                />
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.orderDateCells}
                                                data-test-selector="orderHistoryTable_tableCell_date"
                                            >
                                                <OrderDetailPageTypeLink title={date} orderNumber={linkOrderNumber} />
                                            </DataTableCell>
                                            <DataTableCell {...styles.shipToCells}>{po}</DataTableCell>
                                            <DataTableCell {...styles.shipToCells}>{shipTo}</DataTableCell>

                                            {shouldShowStatus ? (
                                                <DataTableCell
                                                    {...styles.statusCells}
                                                    data-test-selector="orderHistoryTable_tableCell_status"
                                                >
                                                    <OrderDetailPageTypeLink
                                                        title={status}
                                                        orderNumber={linkOrderNumber}
                                                    />
                                                </DataTableCell>
                                            ) : null}
                                            <DataTableCell {...styles.orderTotalCells}>
                                                <OrderDetailPageTypeLink title={total} orderNumber={linkOrderNumber} />
                                            </DataTableCell>
                                            {this.props.fields.showReorderProducts && (
                                                <DataTableCell {...styles.reorderCells}>
                                                    {this.props.isReordering[reorderOrderNumber] && (
                                                        <LoadingSpinner {...styles.reorderButtonSpinner} />
                                                    )}
                                                    {!this.props.isReordering[reorderOrderNumber] && (
                                                        <Button
                                                            disabled={Object.keys(this.props.isReordering).length > 0}
                                                            {...styles.reorderButton}
                                                            // Xng-change - EIS-4192 - adding aria-label for the button without text
                                                            aria-label={translate("reorder")}
                                                            onClick={() =>
                                                                this.props.reorder({
                                                                    orderNumber: reorderOrderNumber,
                                                                    onSuccess: () =>
                                                                        this.onReorderSuccess(
                                                                            reorderOrderNumber,
                                                                            linkOrderNumber,
                                                                        ),
                                                                })
                                                            }
                                                        >
                                                            <Icon src={ShoppingCart} size={22} />
                                                        </Button>
                                                    )}
                                                </DataTableCell>
                                            )}
                                        </DataTableRow>
                                    );
                                },
                            )}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
            </SectionWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(RecentOrders)),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: translate("Recent Orders"),
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.showReorderProducts,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
